// IndexedDB wrapper for offline storage

const DB_NAME = 'WarehouseOpsDB';
const DB_VERSION = 1;
const STORES = {
  INWARD_ENTRIES: 'inward_entries',
  PENDING_SYNC: 'pending_sync',
  CODE_SEQUENCES: 'code_sequences'
};

class OfflineStorage {
  constructor() {
    this.db = null;
    this.initPromise = this.initDB();
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Inward entries store
        if (!db.objectStoreNames.contains(STORES.INWARD_ENTRIES)) {
          const entriesStore = db.createObjectStore(STORES.INWARD_ENTRIES, {
            keyPath: 'entry_id'
          });
          entriesStore.createIndex('timestamp', 'timestamp', { unique: false });
          entriesStore.createIndex('synced', 'synced', { unique: false });
        }

        // Pending sync queue store
        if (!db.objectStoreNames.contains(STORES.PENDING_SYNC)) {
          const syncStore = db.createObjectStore(STORES.PENDING_SYNC, {
            keyPath: 'id',
            autoIncrement: true
          });
          syncStore.createIndex('created_at', 'created_at', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
        }

        // Code sequences store (for offline code generation)
        if (!db.objectStoreNames.contains(STORES.CODE_SEQUENCES)) {
          db.createObjectStore(STORES.CODE_SEQUENCES, { keyPath: 'code_type' });
        }
      };
    });
  }

  async ensureDB() {
    if (!this.db) {
      await this.initPromise;
    }
    return this.db;
  }

  // Inward Entries Operations
  async saveInwardEntry(entry) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readwrite');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const request = store.put(entry);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getInwardEntries(limit = 100) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readonly');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const index = store.index('timestamp');
      const request = index.openCursor(null, 'prev'); // Newest first

      const entries = [];
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor && entries.length < limit) {
          entries.push(cursor.value);
          cursor.continue();
        } else {
          resolve(entries);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedEntries() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readonly');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const request = store.getAll();

      request.onsuccess = () => {
        const allEntries = request.result;
        const unsyncedEntries = allEntries.filter(entry => entry.synced === false);
        resolve(unsyncedEntries);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async markAsSynced(entry_id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readwrite');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const request = store.get(entry_id);

      request.onsuccess = () => {
        const entry = request.result;
        if (entry) {
          entry.synced = true;
          entry.synced_at = new Date().toISOString();
          store.put(entry);
          resolve(true);
        } else {
          resolve(false);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Pending Sync Queue Operations
  async addToSyncQueue(data) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const request = store.add({
        ...data,
        created_at: new Date().toISOString(),
        status: 'pending',
        retry_count: 0
      });

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSyncItems() {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const index = store.index('status');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeSyncItem(id) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSyncItemStatus(id, status, error = null) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SYNC], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SYNC);
      const request = store.get(id);

      request.onsuccess = () => {
        const item = request.result;
        if (item) {
          item.status = status;
          item.retry_count = (item.retry_count || 0) + 1;
          item.last_error = error;
          item.updated_at = new Date().toISOString();
          store.put(item);
          resolve(true);
        } else {
          resolve(false);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Code Sequences (for offline code generation)
  async getCodeSequence(code_type) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CODE_SEQUENCES], 'readonly');
      const store = transaction.objectStore(STORES.CODE_SEQUENCES);
      const request = store.get(code_type);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateCodeSequence(code_type, current_number) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CODE_SEQUENCES], 'readwrite');
      const store = transaction.objectStore(STORES.CODE_SEQUENCES);
      const request = store.put({
        code_type,
        current_number,
        fiscal_year: '25-26',
        updated_at: new Date().toISOString()
      });

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Validation methods for offline mode
  async checkDocumentCodeExists(documentCode) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readonly');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result;
        const exists = entries.some(entry => entry.document_barcode === documentCode);
        resolve(exists);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async checkBundleCodeExists(bundleCode) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.INWARD_ENTRIES], 'readonly');
      const store = transaction.objectStore(STORES.INWARD_ENTRIES);
      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result;
        const exists = entries.some(entry => 
          entry.bundles && entry.bundles.some(bundle => bundle.bundle_barcode === bundleCode)
        );
        resolve(exists);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Clear all data (for testing/reset)
  async clearAll() {
    const db = await this.ensureDB();
    return Promise.all([
      this.clearStore(STORES.INWARD_ENTRIES),
      this.clearStore(STORES.PENDING_SYNC),
      this.clearStore(STORES.CODE_SEQUENCES)
    ]);
  }

  async clearStore(storeName) {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }
}

export const offlineStorage = new OfflineStorage();